import React, { useEffect, useMemo, useRef, useState } from 'react';
import useGetSpotReviews from '../../services/hooks/useGetSpotReviews';
import useSearchSpotReviews from '../../services/hooks/useSearchSpotReviews';
import useOnScreen from '../../services/hooks/useOnScreen';
import SortReviews from './sort-reviews';
import SearchBar from './search-bar';
import Review from '../review';
import SvgStar from 'src/assets/svg/SvgStar.svg';
import { formatRating, formatReviewCount } from '../../helpers/rating';
import { highlightReviews } from '../../helpers/searchHighlight';

const perPage = 10;

const AllReviews = ({
    reviewsCount,
    rating,
    userDetails,
    spotId,
    host,
    sortReviews,
    changeSort,
    isSpotDetail,
    handleSniffpassReviewBadge,
    spot,
    noRichText,
    isEnhancedReviewTest,
}) => {
    const { getSpotReviews, reviews, loading } = useGetSpotReviews();
    const { searchSpotReviews, searchResults, loading: searchLoading, error: searchError } = useSearchSpotReviews();
    const [limit, setLimit] = useState(perPage);
    const [allReviews, setAllReviews] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [modalSortOpen, setModalSortOpen] = useState(false);
    const myRef = useRef(null);
    const isVisible = useOnScreen(myRef);

    useEffect(() => {
        if (!isVisible || limit === reviewsCount) {
            return;
        }
        const newLimit = limit + perPage >= reviewsCount ? reviewsCount : limit + perPage;
        getSpotReviews({
            variables: {
                id: spotId,
                first: newLimit,
                sortBy: sortReviews.sortBy,
                sortOrder: sortReviews.sortOrder,
            },
        });
        setLimit(newLimit);
    }, [isVisible]);

    useEffect(() => {
        if (reviews) {
            setAllReviews(reviews);
        }
    }, [reviews]);

    useEffect(() => {
        setLimit(perPage);

        // If we have an active search, re-execute the search with new sort parameters
        if (isEnhancedReviewTest && searchQuery) {
            handleSearch(searchQuery);
        } else {
            getSpotReviews({
                variables: {
                    id: spotId,
                    first: perPage,
                    sortBy: sortReviews.sortBy,
                    sortOrder: sortReviews.sortOrder,
                },
            });
        }
    }, [sortReviews]);

    const handleSearch = async (query) => {
        if (!isEnhancedReviewTest) {
            return;
        }

        if (!query.trim()) {
            handleClearSearch();
            return;
        }

        setSearchQuery(query);

        try {
            await searchSpotReviews({
                variables: {
                    spotId,
                    searchText: query.trim(),
                    sortBy: sortReviews.sortBy,
                    sortOrder: sortReviews.sortOrder,
                },
            });
        } catch (error) {
            console.error('Search error:', error);
        }
    };

    const handleClearSearch = () => {
        if (!isEnhancedReviewTest) {
            return;
        }

        setSearchQuery('');
    };

    const toggleModalSortOpen = () => setModalSortOpen(!modalSortOpen);

    const handleModalSortChange = (v) => {
        changeSort(v);
        setModalSortOpen(false);
    };

    // Apply highlighting to search results
    const highlightedSearchResults = useMemo(() => {
        if (!searchResults || !searchQuery) {
            return searchResults;
        }
        return highlightReviews(searchResults, searchQuery);
    }, [searchResults, searchQuery]);

    const displayReviews = isEnhancedReviewTest && searchQuery ? highlightedSearchResults || [] : allReviews;

    return (
        <div className="comments">
            <div className="rev-head">
                <div className="snif-m1 snif-semibold mt-2 mb-3">
                    <SvgStar className="star-reviews" /> {formatRating(rating)}
                    <span className="reviews-count snif-medium">{` (${formatReviewCount(reviewsCount)} Reviews)`}</span>
                </div>
            </div>
            {isEnhancedReviewTest && (
                <SearchBar
                    placeholder="Search reviews"
                    onSearch={handleSearch}
                    onClear={handleClearSearch}
                    autoFocus={true}
                    initialValue={searchQuery}
                />
            )}
            <SortReviews
                {...{
                    toggleSortOpen: toggleModalSortOpen,
                    sortOpen: modalSortOpen,
                    onChangeSort: handleModalSortChange,
                    sortReviews,
                }}
            />
            {isEnhancedReviewTest && searchQuery && displayReviews.length === 0 && !searchLoading && (
                <div className="no-results-message snif-p text-center mt-4 mb-4">No results found. Try changing your search keyword.</div>
            )}
            {searchError && <div className="error-message snif-p text-center mt-4 mb-4 text-danger">Error searching reviews. Please try again.</div>}
            {displayReviews.length > 0 &&
                displayReviews.map((review) => (
                    <Review
                        key={review.id}
                        {...{
                            ...review,
                            host,
                            spot,
                            spotId,
                            userDetails,
                            isSpotDetail,
                            handleSniffpassReviewBadge,
                            noRichText,
                            hasHighlightedContent: isEnhancedReviewTest && searchQuery && !!searchResults,
                        }}
                    />
                ))}
            {(!isEnhancedReviewTest || !searchQuery) && <div ref={myRef} />}
            {(loading || searchLoading) && (
                <div className="col-md-12 mb-3 text-center">
                    <div className="loading" />
                </div>
            )}
        </div>
    );
};

export default AllReviews;
